import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';
import Pagination from '../../components/Pagination';
import {
  CREATE_CATEGORIES,
  DEFAULT_FORM,
  PRODUCT_TABS,
  STATUS_FILTER_OPTIONS,
  categorySymbolParams,
  filterSymbolsForCategory,
  isTabDisabled,
  tabQueryParams,
} from './productConstants';
import {
  OrderProductCreateModal,
  OrderProductDetailModal,
  OrderProductEditModal,
} from './OrderProductModals';
import TimedDurationModal from './TimedDurationModal';
import {
  buildCreatePayload,
  buildUpdatePayload,
  parsePlayConfig,
  playTypeLabel,
  productToForm,
  resolveProductStatus,
  statusDisplayText,
  statusFilterToParams,
} from './productFormUtils';

function buildPageParams(pageNo, tabKey, { filterStatus, filterSymbol, filterPlayType }) {
  const params = {
    pageNo,
    pageSize: 15,
    ...tabQueryParams(tabKey),
    ...statusFilterToParams(filterStatus),
  };
  if (filterSymbol) {
    params.marketSymbolCode = filterSymbol;
  }
  if (filterPlayType) {
    params.playType = filterPlayType;
  }
  return params;
}

export default function OrderProductListPage() {
  const [activeTab, setActiveTab] = useState('ALL');
  const [tabCounts, setTabCounts] = useState({});
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  const [filterStatus, setFilterStatus] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterPlayType, setFilterPlayType] = useState('');
  const [symbolOptions, setSymbolOptions] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createCategory, setCreateCategory] = useState('TIME_LIMITED');
  const [createForm, setCreateForm] = useState(DEFAULT_FORM);
  const [createSymbols, setCreateSymbols] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [timedDurationOpen, setTimedDurationOpen] = useState(false);
  const [timedDurationProduct, setTimedDurationProduct] = useState(null);

  const loadRecords = useCallback(async (pageNo = 1, tabKey = 'ALL', filterOverrides) => {
    setLoading(true);
    setError('');
    const filters = filterOverrides ?? { filterStatus, filterSymbol, filterPlayType };
    try {
      const params = buildPageParams(pageNo, tabKey, filters);
      const data = await adminApi.orderProductPage(params);
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      if (data?.tabCounts) {
        setTabCounts(data.tabCounts);
      }
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSymbol, filterPlayType]);

  /** 全查並更新各玩法 Tab 數量（僅新增商品後呼叫） */
  const refreshTabCounts = useCallback(async () => {
    try {
      const params = buildPageParams(1, 'ALL', {
        filterStatus: '',
        filterSymbol: '',
        filterPlayType: '',
      });
      const data = await adminApi.orderProductPage(params);
      if (data?.tabCounts) {
        setTabCounts(data.tabCounts);
      }
    } catch {
      // 靜默失敗，不影響列表操作
    }
  }, []);

  useEffect(() => {
    loadRecords(1, 'ALL');
    // 僅首次進入頁面載入；Tab 切換與搜索由使用者操作觸發
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    adminApi.orderProductMarketSymbols().then((list) => {
      setSymbolOptions(Array.isArray(list) ? list : []);
    }).catch(() => setSymbolOptions([]));
  }, []);

  async function loadCreateSymbols(categoryKey) {
    setSymbolsLoading(true);
    try {
      const list = await adminApi.orderProductMarketSymbols(categorySymbolParams(categoryKey));
      const normalized = Array.isArray(list) ? list : [];
      setCreateSymbols(filterSymbolsForCategory(normalized, categoryKey));
    } catch {
      setCreateSymbols([]);
    } finally {
      setSymbolsLoading(false);
    }
  }

  function openCreateModal() {
    setCreateCategory('TIME_LIMITED');
    setCreateForm({ ...DEFAULT_FORM });
    setCreateOpen(true);
    setActionMsg('');
    loadCreateSymbols('TIME_LIMITED');
  }

  function handleCreateCategoryChange(categoryKey) {
    setCreateCategory(categoryKey);
    setCreateForm((prev) => ({ ...prev, marketSymbolCode: '' }));
    loadCreateSymbols(categoryKey);
  }

  async function handleCreate(event) {
    event.preventDefault();
    if (!createForm.marketSymbolCode) {
      setActionMsg('請選擇交易對');
      return;
    }
    setSubmitting(true);
    setActionMsg('');
    try {
      const payload = buildCreatePayload(createForm, createCategory, CREATE_CATEGORIES);
      await adminApi.createOrderProduct(payload);
      setActionMsg('新增成功（預設禁用，請於列表啟用）');
      setCreateOpen(false);
      await refreshTabCounts();
      loadRecords(page, activeTab);
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.message : '新增失敗');
    } finally {
      setSubmitting(false);
    }
  }

  async function openTimedDurations(row) {
    setActionMsg('');
    setTimedDurationProduct(row);
    setTimedDurationOpen(true);
  }

  async function openDetail(row) {
    setActionMsg('');
    try {
      const product = await adminApi.orderProductDetail(row.id, row.playType);
      setActiveProduct(product);
      setDetailForm(productToForm(product));
      if (product.playType === 'FUTURES') {
        const list = await adminApi.listFuturesPeriods(product.id);
        setPeriods(Array.isArray(list) ? list : []);
      } else {
        setPeriods([]);
      }
      setDetailOpen(true);
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.message : '載入詳情失敗');
    }
  }

  function openEditFromDetail() {
    setEditForm({ ...detailForm });
    setDetailOpen(false);
    setEditOpen(true);
  }

  async function handleEditSubmit(event) {
    event.preventDefault();
    if (!activeProduct || !editForm) {
      return;
    }
    setSubmitting(true);
    setActionMsg('');
    try {
      const payload = buildUpdatePayload(activeProduct, editForm);
      await adminApi.updateOrderProduct(payload);
      setActionMsg('更新成功');
      setEditOpen(false);
      loadRecords(page, activeTab);
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.message : '更新失敗');
    } finally {
      setSubmitting(false);
    }
  }

  function handleSearch(event) {
    event.preventDefault();
    loadRecords(1, activeTab);
  }

  function handleResetFilters() {
    setFilterStatus('');
    setFilterSymbol('');
    setFilterPlayType('');
    setActiveTab('ALL');
    loadRecords(1, 'ALL', { filterStatus: '', filterSymbol: '', filterPlayType: '' });
  }

  function handleTabClick(tabKey) {
    if (isTabDisabled(tabKey, tabCounts)) {
      return;
    }
    setActiveTab(tabKey);
    loadRecords(1, tabKey);
  }

  return (
    <div className="page-card product-list-page">
      <div className="product-list-page__toolbar">
        <form className="filter-bar product-list-page__filters" onSubmit={handleSearch}>
          <div className="form-field">
            <label htmlFor="filterStatus">狀態</label>
            <select
              id="filterStatus"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {STATUS_FILTER_OPTIONS.map((item) => (
                <option key={item.value || 'all'} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="filterSymbol">交易對</label>
            <select
              id="filterSymbol"
              value={filterSymbol}
              onChange={(e) => setFilterSymbol(e.target.value)}
            >
              <option value="">全部</option>
              {symbolOptions.map((symbol) => (
                <option key={symbol.code} value={symbol.code}>
                  {symbol.displayName || symbol.code}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="filterPlayType">交易類型</label>
            <select
              id="filterPlayType"
              value={filterPlayType}
              onChange={(e) => setFilterPlayType(e.target.value)}
            >
              <option value="">全部</option>
              <option value="TIME_LIMITED">限時</option>
              <option value="FUTURES">交割</option>
              <option value="PERPETUAL">永續</option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary">搜索</button>
          <button type="button" className="btn" onClick={handleResetFilters}>重置</button>
          <button type="button" className="btn btn-primary" onClick={openCreateModal}>新增</button>
        </form>
      </div>

      <div className="product-tabs">
        {PRODUCT_TABS.map((tab) => {
          const disabled = isTabDisabled(tab.key, tabCounts);
          return (
            <button
              key={tab.key}
              type="button"
              className={`product-tabs__item ${activeTab === tab.key ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
              onClick={() => handleTabClick(tab.key)}
              disabled={disabled}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {actionMsg && (
        <div className={`alert ${actionMsg.includes('失敗') ? 'alert-error' : 'alert-success'}`}>
          {actionMsg}
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>交易對</th>
            <th>自定義名稱</th>
            <th>交易類型</th>
            <th>狀態</th>
            <th>顯示順序</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {!loading && records.length === 0 ? (
            <tr>
              <td colSpan={6} className="empty-cell">尚無商品</td>
            </tr>
          ) : (
            records.map((row) => {
              const cfg = parsePlayConfig(row.playConfig);
              const customName = cfg.customName;
              const statusKey = resolveProductStatus(row.status, row.exchangeStatus);
              return (
                <tr key={row.id}>
                  <td>{row.name || '—'}</td>
                  <td>{customName || '—'}</td>
                  <td>{playTypeLabel(row.playType)}</td>
                  <td>
                    <span className={`status-pill status-pill--${statusKey}`}>
                      {statusDisplayText(row.status, row.exchangeStatus)}
                    </span>
                  </td>
                  <td>{row.sort ?? '—'}</td>
                  <td className="action-cell">
                    <button type="button" className="btn btn-sm" onClick={() => openDetail(row)}>
                      交易詳情
                    </button>
                    {(row.playType === 'FUTURES' || row.playType === 'TIME_LIMITED') && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => (
                          row.playType === 'TIME_LIMITED'
                            ? openTimedDurations(row)
                            : openDetail(row)
                        )}
                      >
                        產品細節
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, activeTab)} />

      <OrderProductCreateModal
        open={createOpen}
        categoryKey={createCategory}
        form={createForm}
        symbols={createSymbols}
        symbolsLoading={symbolsLoading}
        submitting={submitting}
        onClose={() => setCreateOpen(false)}
        onCategoryChange={handleCreateCategoryChange}
        onChange={(field, value) => setCreateForm((prev) => ({ ...prev, [field]: value }))}
        onSubmit={handleCreate}
      />

      <OrderProductDetailModal
        open={detailOpen}
        product={activeProduct}
        form={detailForm}
        periods={periods}
        onClose={() => setDetailOpen(false)}
        onEdit={openEditFromDetail}
      />

      <OrderProductEditModal
        open={editOpen}
        product={activeProduct}
        form={editForm || {}}
        symbols={symbolOptions}
        submitting={submitting}
        onClose={() => setEditOpen(false)}
        onChange={(field, value) => setEditForm((prev) => ({ ...prev, [field]: value }))}
        onReset={() => setEditForm(productToForm(activeProduct))}
        onSubmit={handleEditSubmit}
      />

      <TimedDurationModal
        open={timedDurationOpen}
        product={timedDurationProduct}
        onClose={() => {
          setTimedDurationOpen(false);
          setTimedDurationProduct(null);
        }}
      />
    </div>
  );
}
